import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import get from 'lodash/get';
import { Price } from '@grandvisionhq/common';
import { usePaymentsUIContext } from '../../../context/PaymentsUI/context.js';
import { useGetAdditionalCartCost } from '../../../hooks/use-get-additional-cart-costs.js';
import { useGetCartPaymentMethods } from '../../../hooks/use-get-cart-payment-methods.js';
import { usePaymentMethodLabelsMap } from '../../../hooks/use-payment-method-labels-map.js';
import { usePaymentMethodForm } from './use-payment-method-form.js';
import { getRestPaymentMethodName } from '../utils/get-payment-method-name.js';
import { PaymentForm } from '../../../component/PaymentForm/PaymentForm.js';

var useGetPaymentMethodList = (cartId, onPaymentCompleteHandler)=>{
    const paymentMethodLabelsMap = usePaymentMethodLabelsMap();
    const [getPaymentMethods, queryResult] = useGetCartPaymentMethods();
    const { getAdditionalCosts , additionalCosts  } = useGetAdditionalCartCost();
    const { isProcessingOrder  } = usePaymentsUIContext();
    React.useEffect(()=>{
        if (cartId) {
            getPaymentMethods(cartId);
            getAdditionalCosts();
        }
    }, [
        cartId,
        getPaymentMethods,
        getAdditionalCosts
    ]);
    const [renderPaymentForm, selectedPaymentDetails, resetPaymentDetails] = usePaymentMethodForm();
    const paymentMethods = React.useMemo(()=>{
        return queryResult === null || queryResult === void 0 ? void 0 : queryResult.data;
    }, [
        queryResult === null || queryResult === void 0 ? void 0 : queryResult.data
    ]);
    const error = React.useMemo(()=>{
        return queryResult === null || queryResult === void 0 ? void 0 : queryResult.error;
    }, [
        queryResult === null || queryResult === void 0 ? void 0 : queryResult.error
    ]);
    const loading = React.useMemo(()=>{
        return queryResult === null || queryResult === void 0 ? void 0 : queryResult.loading;
    }, [
        queryResult === null || queryResult === void 0 ? void 0 : queryResult.loading
    ]);
    const called = React.useMemo(()=>{
        return queryResult === null || queryResult === void 0 ? void 0 : queryResult.called;
    }, [
        queryResult === null || queryResult === void 0 ? void 0 : queryResult.called
    ]);
    const selectedPaymentData = React.useMemo(()=>selectedPaymentDetails
    , [
        selectedPaymentDetails
    ]);
    const onHandlePaymentAction = React.useCallback(()=>{
        onPaymentCompleteHandler(selectedPaymentDetails);
    }, [
        onPaymentCompleteHandler,
        selectedPaymentDetails
    ]);
    const paymentMethodItems = React.useMemo(()=>{
        if (!paymentMethods || error) {
            return [];
        }
        const items = paymentMethods.map((_param)=>{
            var { provider , name , icons  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
                "provider",
                "name",
                "icons"
            ]);
            var ref, ref1;
            const mappedApiName = getRestPaymentMethodName(name);
            let item = React.Children.toArray(icons) // Call toArray so we can manipulate the data with type safety
            ;
            // Asides are provided as an array of thumbnail images for example.
            // Loop over additionalCosts to bind them to the aside array and return the combination.
            if (additionalCosts && additionalCosts.length) {
                additionalCosts.forEach((additionalCost, i)=>{
                    if (additionalCost.key === mappedApiName) {
                        const price = get(additionalCost, 'masterVariant.prices[0].value.centAmount', 0);
                        if (price) {
                            item = [
                                /*#__PURE__*/ React.createElement("span", {
                                    key: `${additionalCost.key}-aside-${i}`,
                                    className: "checkout-payment-methods__additional-cost"
                                }, "+ ", /*#__PURE__*/ React.createElement(Price, {
                                    value: price
                                })),
                                ...item !== null && item !== void 0 ? item : [], 
                            ];
                        }
                    }
                });
            }
            let formConfig;
            const paymentMethodList = (ref = rest) === null || ref === void 0 ? void 0 : ref.items;
            const cardBrands = (ref1 = rest) === null || ref1 === void 0 ? void 0 : ref1.brands;
            if (paymentMethodList) {
                formConfig = {
                    items: paymentMethodList
                };
            }
            if (cardBrands) {
                formConfig = {
                    brands: cardBrands
                };
            }
            const formComponent = renderPaymentForm({
                type: name,
                provider,
                config: formConfig
            });
            return {
                value: name,
                label: paymentMethodLabelsMap[name],
                id: `${provider}-${name}`,
                disabled: isProcessingOrder,
                aside: item,
                content: /*#__PURE__*/ React.createElement(PaymentForm, {
                    paymentMethod: name,
                    form: formComponent,
                    onHandlePaymentAction: onHandlePaymentAction,
                    isActionDisabled: false,
                    isActionLoading: false
                })
            };
        });
        return items;
    }, [
        paymentMethods,
        error,
        additionalCosts,
        renderPaymentForm,
        paymentMethodLabelsMap,
        onHandlePaymentAction,
        isProcessingOrder, 
    ]);
    return [
        React.useMemo(()=>paymentMethodItems
        , [
            paymentMethodItems
        ]),
        selectedPaymentData,
        resetPaymentDetails,
        additionalCosts,
        {
            error,
            called,
            loading,
            data: paymentMethods || []
        }, 
    ];
};

export { useGetPaymentMethodList };
//# sourceMappingURL=use-get-payment-method-list.js.map
